import React from "react";
import { DollarOutlined, RiseOutlined, GiftOutlined } from "@ant-design/icons";
import {
  CompanyCardData,
  EarnMetric,
  MetricTile as MetricTileType,
} from "./types";
import { SectionBlock, MetricTile } from "./shared";

interface Props {
  data: Pick<
    CompanyCardData,
    "price_rise_explanation" | "dividend_explanation" | "earn_metrics"
  >;
}

// A negative P/E means the company made a loss, so the ratio can't be read
// as "years to earn back the price". Keep the number but flag it.
const withPeNote = (tile: EarnMetric): MetricTileType => {
  const isPe = /price\s*to\s*earnings|p\/e/i.test(tile.term);
  const value = parseFloat(tile.value.replace(/[^0-9.-]/g, ""));
  return isPe && value < 0
    ? { ...tile, note: "Not meaningful · the company made a loss" }
    : tile;
};

const HowYouEarn: React.FC<Props> = ({ data }) => (
  <SectionBlock
    icon={<DollarOutlined />}
    title="How you could earn from it"
    subtitle="There are two separate ways"
  >
    <div className="grid grid-cols-2 gap-3 mt-4 sm:grid-cols-1">
      <div className="border border-grey rounded-[13px] p-4">
        <div className="flex items-center gap-2 font-semibold text-sm text-neutral font-dm mb-1.5">
          <RiseOutlined className="text-primary" />
          The price rises
        </div>
        <p className="text-[13px] text-neutral/70 font-dm m-0">
          {data.price_rise_explanation}
        </p>
      </div>

      <div className="border border-grey rounded-[13px] p-4">
        <div className="flex items-center gap-2 font-semibold text-sm text-neutral font-dm mb-1.5">
          <GiftOutlined className="text-primary" />
          It pays you a dividend
        </div>
        <p
          className="text-[13px] text-neutral/70 font-dm m-0"
          dangerouslySetInnerHTML={{ __html: data.dividend_explanation }}
        />
      </div>
    </div>

    <div className="grid grid-cols-3 gap-3 mt-4 sm:grid-cols-2">
      {data.earn_metrics.map((tile, i) => (
        <MetricTile key={i} tile={withPeNote(tile)} />
      ))}
    </div>
  </SectionBlock>
);

export default HowYouEarn;
